import clsx from 'clsx';
import { FunctionComponent, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { API } from '../../api/rest';
import { Account } from '../../types/Account';
import { Form, FormBody, Input, Label } from '../Base';
import { useTurnstile } from '../Turnstile';
import Button from '../atoms/button';

type BaseInputCfg = {
  label: string;
  description?: string;
  id: string;
  required?: boolean;
};

type NormalInputCfg = BaseInputCfg & {
  type: JSX.IntrinsicElements['input']['type'] | 'longtext';
};

type ChoiceInputCfg = BaseInputCfg & {
  type: 'choice';
  choices: string[];
};

type MultiChoiceInputCfg = BaseInputCfg & {
  type: 'multichoice';
  choices: string[];
  // display labels keyed by choice, falls back to the raw choice
  choiceLabels?: Record<string, string>;
};

type InputCfg = NormalInputCfg | ChoiceInputCfg | MultiChoiceInputCfg;

const AUTO_SCROLL_CLASS = 'auto-scroll-to';
const HEADER_HEIGHT = 80;

// option keys from the extension's `typed-options-store.ts` (`experimental: true`)
const BETA_OPTION_LABELS: Record<string, string> = {
  filename_template: 'Custom filename',
  pdf_insert_links: 'Active links in PDFs',
};

// DUPED in fpaccount account.betaFeedback.handlers.js
const FEEDBACK_CHOICES = ['good', 'ok', 'bad'];
const BETA_OPTIONS_ID = 'beta_options';
const FEEDBACK_ID = 'feedback';
const EMAIL_ID = 'email';
const MORE_INFO_ID = 'more_info';
const FOLLOW_UP_ID = 'follow_up';

const FIELDS: InputCfg[] = [
  {
    label: 'Which beta features are you using? (multi-select)',
    id: BETA_OPTIONS_ID,
    type: 'multichoice',
    // TODO: support retired keys that keep a label but are only offered as a
    // choice when the URL passes them explicitly
    choices: Object.keys(BETA_OPTION_LABELS),
    choiceLabels: BETA_OPTION_LABELS,
  },
  {
    label: 'How are the beta features working for you?',
    id: FEEDBACK_ID,
    type: 'choice',
    choices: FEEDBACK_CHOICES,
    required: true,
  },
  { label: 'Email Address', id: EMAIL_ID, type: 'email', required: true },
  { label: 'Tell us more', id: MORE_INFO_ID, type: 'longtext' },
  {
    label:
      'Would you be open to a follow-up conversation? This is entirely optional and helps us improve GoFullPage.',
    id: FOLLOW_UP_ID,
    type: 'choice',
    choices: ['yes', 'no'],
  },
];

type BetaFeedbackFormProps = {
  account: Account;
};

export const BetaFeedbackForm = ({ account }: BetaFeedbackFormProps) => {
  const location = useLocation();
  const params = new URLSearchParams(location.search);

  const initialFeedback = params.get('f');
  // passed through from the extension, not shown or editable
  const version = params.get('v') || undefined;
  const defaultFormData = {
    [EMAIL_ID]: account.email,
    [BETA_OPTIONS_ID]: params.getAll('opt'),
    ...(initialFeedback ? { [FEEDBACK_ID]: initialFeedback } : {}),
  };

  const [formData, setFormData] =
    useState<Record<string, undefined | string | string[]>>(defaultFormData);
  const [msg, setMsg] = useState<{
    text: string;
    type: 'success' | 'error';
  } | null>(null);
  const [loading, setLoading] = useState(false);
  const captcha = useTurnstile();
  const hideForm = msg?.type === 'success';

  // useEffect(() => {
  //   setMsg({
  //     type: 'success',
  //     text: 'Hey this is a message, what do you think about this?',
  //   });
  //   window.setTimeout(() => _scrollToError(), 50);
  // }, []);

  const onSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (loading) {
      return;
    }
    if (!captcha.token) {
      setMsg({
        type: 'error',
        text: 'Please complete the captcha before submitting.',
      });
      window.setTimeout(() => _scrollToError(), 50);
      return;
    }
    setLoading(true);

    API.post({
      path: '/account/beta-feedback',
      body: { ...formData, version, captcha_token: captcha.token },
    })
      .then((resp) => {
        if (resp.type === 'success') {
          setMsg({
            type: 'success',
            text: 'Thank you for your feedback! You may close this page.',
          });
          window.scrollTo(0, 0);
        } else {
          setMsg({
            type: 'error',
            text:
              resp.message ||
              `Unknown error, please contact support@gofullpage.com`,
          });
          // HACK - scroll to error
          window.setTimeout(() => _scrollToError(), 50);
        }
      })
      .catch((error) => {
        setMsg({
          type: 'error',
          text: `Unknown error (${error?.message || 'cannot access message'}), please contact support@gofullpage.com`,
        });
        window.setTimeout(() => _scrollToError(), 50);
      })
      .finally(() => {
        setLoading(false);
        captcha.reset();
      });
  };

  return (
    <div className="rounded-md border border-solid border-gray-200 bg-white p-8 text-left">
      <Form onSubmit={onSubmit}>
        <FormBody className="space-y-6">
          {msg && (
            <div
              className={clsx(
                AUTO_SCROLL_CLASS,
                'rounded-md p-4',
                msg.type === 'error'
                  ? 'bg-red-50 text-red-700'
                  : 'bg-green-50 text-green-800',
              )}
            >
              {msg.text}
            </div>
          )}
          {!hideForm &&
            FIELDS.map((field) => {
              const { label, description, id, required } = field;
              const rawValue = formData[id];
              const value = typeof rawValue === 'string' ? rawValue : '';
              const values = Array.isArray(rawValue) ? rawValue : [];
              const onChange = (
                e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>,
              ) => {
                const value = e.target.value;
                setFormData((prevState) => ({
                  ...prevState,
                  [id]: value,
                }));
              };
              const onToggle = (choice: string, checked: boolean) => {
                setFormData((prevState) => {
                  const prev = prevState[id];
                  const prevValues = Array.isArray(prev) ? prev : [];
                  return {
                    ...prevState,
                    [id]: checked
                      ? [...prevValues, choice]
                      : prevValues.filter((v) => v !== choice),
                  };
                });
              };

              return (
                <div key={id} className="space-y-2">
                  <Label
                    className="block font-medium"
                    id={`${id}-label`}
                    htmlFor={
                      _isChoice(field) || _isMultiChoice(field) ? undefined : id
                    }
                  >
                    {label}
                    {required && <Required />}
                  </Label>
                  {Boolean(description) && (
                    <p className="!mt-0 text-sm text-gray-500">{description}</p>
                  )}
                  {_isChoice(field) ? (
                    <ChoiceInput
                      id={id}
                      choices={field.choices}
                      value={value}
                      onChange={onChange}
                      required={required}
                    />
                  ) : _isMultiChoice(field) ? (
                    <MultiChoiceInput
                      id={id}
                      // also show selected values missing from `choices`, e.g.
                      // options newer than this list
                      choices={[...new Set([...field.choices, ...values])]}
                      choiceLabels={field.choiceLabels}
                      values={values}
                      onToggle={onToggle}
                    />
                  ) : field.type === 'longtext' ? (
                    <textarea
                      className="w-full font-sans"
                      id={id}
                      name={id}
                      value={value}
                      onChange={onChange}
                      required={required}
                      // TODO - auto-height
                      rows={3}
                    />
                  ) : (
                    <Input
                      className="w-full"
                      id={id}
                      name={id}
                      type={field.type}
                      value={value}
                      onChange={onChange}
                      required={required}
                    />
                  )}
                </div>
              );
            })}
        </FormBody>
        {!hideForm && (
          <>
            <div className="mb-4">{captcha.widget}</div>
            <Button loading={loading} type="submit" disabled={loading}>
              Submit
            </Button>
          </>
        )}
      </Form>
    </div>
  );
};

// ## Helpers

const Required: FunctionComponent<Record<string, never>> = () => {
  return <span className="ml-0.5 text-red-600">*</span>;
};

const ChoiceInput: FunctionComponent<{
  id: string;
  choices: string[];
  value: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  required?: boolean;
}> = ({ id, choices, value, onChange, required }) => {
  return (
    <div
      role="radiogroup"
      aria-labelledby={`${id}-label`}
      className="flex flex-wrap gap-2"
    >
      {choices.map((choice) => (
        <label key={choice} className={PILL_LABEL_CLASS}>
          <input
            className="peer sr-only"
            type="radio"
            name={id}
            value={choice}
            checked={value === choice}
            onChange={onChange}
            required={required}
          />
          <span className={PILL_CLASS}>{choice}</span>
        </label>
      ))}
    </div>
  );
};

const MultiChoiceInput: FunctionComponent<{
  id: string;
  choices: string[];
  choiceLabels?: Record<string, string>;
  values: string[];
  onToggle: (choice: string, checked: boolean) => void;
}> = ({ id, choices, choiceLabels, values, onToggle }) => {
  return (
    <div
      role="group"
      aria-labelledby={`${id}-label`}
      className="flex flex-wrap gap-2"
    >
      {choices.map((choice) => (
        <label key={choice} className={PILL_LABEL_CLASS}>
          <input
            className="peer sr-only"
            type="checkbox"
            name={id}
            value={choice}
            checked={values.includes(choice)}
            onChange={(e) => onToggle(choice, e.target.checked)}
          />
          <span className={PILL_CLASS}>{choiceLabels?.[choice] ?? choice}</span>
        </label>
      ))}
    </div>
  );
};

// `!` overrides the `.pure-form label` margin
const PILL_LABEL_CLASS = clsx('relative !mb-0 !mt-0.5 cursor-pointer');

const PILL_CLASS = clsx(
  'block min-w-[60px] rounded-full border border-solid border-gray-300 bg-white px-4 py-1.5 text-center font-medium text-gray-500 transition-colors hover:border-primary peer-checked:border-primary peer-checked:bg-primary/10 peer-checked:text-primary-800 peer-focus-visible:ring-2 peer-focus-visible:ring-primary-300 peer-focus-visible:ring-offset-2',
);

// input `type` is typed as any string, so `field.type === 'choice'` doesn't
// narrow the union on its own
const _isChoice = (field: InputCfg): field is ChoiceInputCfg =>
  field.type === 'choice';

const _isMultiChoice = (field: InputCfg): field is MultiChoiceInputCfg =>
  field.type === 'multichoice';

const _scrollToError = () => {
  const elt = document.querySelector(`.${AUTO_SCROLL_CLASS}`);
  if (elt) {
    const y =
      elt.getBoundingClientRect().top + window.pageYOffset - HEADER_HEIGHT;

    window.scrollTo({
      top: y,
      behavior: 'smooth',
    });
  }
};
